import { useState, useEffect, useCallback, useRef } from 'react';
import CelebrityImage from './CelebrityImage';
import HintPanel from './HintPanel';
import GuessInput from './GuessInput';
import ChatPanel from './ChatPanel';
import RoundEnd from './RoundEnd';
import MatchEnd from './MatchEnd';

// Watches viewport — updates on resize/rotation
function useIsMobile(bp = 768) {
  const [v, setV] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setV(e.matches);
    mq.addEventListener('change', fn);
    setV(mq.matches);
    return () => mq.removeEventListener('change', fn);
  }, [bp]);
  return v;
}

export default function GameRoom({ roomData, myInfo, emit, onLeave }) {
  // ── All state identical to original ───────────────────────────────────────
  const [phase,               setPhase]             = useState('waiting');
  const [round,               setRound]             = useState(1);
  const [celebrityImage,      setCelebrityImage]    = useState(null);
  const [blur,                setBlur]              = useState(20);
  const [imageRevealed,       setImageRevealed]     = useState(false);
  const [revealedName,        setRevealedName]      = useState('');
  const [hints,               setHints]             = useState([]);
  const [currentHintNumber,   setCurrentHintNumber] = useState(0);
  const [hintTimer,           setHintTimer]         = useState(30);
  const [guessesLeft,         setGuessesLeft]       = useState(3);
  const [answered,            setAnswered]          = useState(false);
  const [forfeited,           setForfeited]         = useState(false);
  const [lastGuessResult,     setLastGuessResult]   = useState(null);
  const [opponentGuessesLeft, setOpponentGuessesLeft] = useState(3);
  const [opponentAnswered,    setOpponentAnswered]  = useState(false);
  const [opponentWrongFlash,  setOpponentWrongFlash]= useState(false);
  const [inactivityWarning,   setInactivityWarning] = useState(null);
  const [roundEndData,        setRoundEndData]      = useState(null);
  const [matchEndData,        setMatchEndData]      = useState(null);
  const [hasReacted,          setHasReacted]        = useState(false);
  const [incomingReaction,    setIncomingReaction]  = useState(null);
  const [messages,            setMessages]          = useState([]);
  const [skipsRemaining,      setSkipsRemaining]    = useState(5);
  const [mySkipVoted,         setMySkipVoted]       = useState(false);
  const [opponentSkipVoted,   setOpponentSkipVoted] = useState(false);
  const [showExitConfirm,     setShowExitConfirm]   = useState(false);

  // Mobile-only UI state
  const [chatOpen,   setChatOpen]   = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);

  const isMobile    = useIsMobile();
  const hintTimerRef = useRef(null);
  const myKey        = roomData?.you?.uid === roomData?.p1?.uid ? 'p1' : 'p2';
  const opponentName = myKey === 'p1' ? roomData?.p2?.name : roomData?.p1?.name;
  const myName       = roomData?.you?.name || myInfo?.name || 'You';

  // ── Timer ─────────────────────────────────────────────────────────────────
  const startHintTimer = useCallback(() => {
    clearInterval(hintTimerRef.current);
    setHintTimer(30);
    hintTimerRef.current = setInterval(() => {
      setHintTimer(t => {
        if (t <= 1) { clearInterval(hintTimerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(hintTimerRef.current), []);

  // ── Events (identical to original) ───────────────────────────────────────
  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'round_start': {
        setPhase('playing');
        setRound(event.round);
        setCelebrityImage(event.celebrity_image);
        setBlur(event.blur ?? 20);
        setImageRevealed(false);
        setRevealedName('');
        setHints([event.hint]);
        setCurrentHintNumber(1);
        setGuessesLeft(3);
        setAnswered(false);
        setForfeited(false);
        setLastGuessResult(null);
        setOpponentGuessesLeft(3);
        setOpponentAnswered(false);
        setHasReacted(false);
        setIncomingReaction(null);
        setMessages([]);
        setMySkipVoted(false);
        setOpponentSkipVoted(false);
        setChatOpen(false);
        setUnreadChat(0);
        if (event.skips_remaining !== undefined) setSkipsRemaining(event.skips_remaining);
        startHintTimer();
        break;
      }
      case 'hint_revealed': {
        setHints(prev => [...prev, event.hint]);
        setCurrentHintNumber(event.hint_number);
        setBlur(event.blur);
        setHasReacted(false);
        setMySkipVoted(false);
        setOpponentSkipVoted(false);
        if (event.skips_remaining !== undefined) setSkipsRemaining(event.skips_remaining);
        startHintTimer();
        break;
      }
      case 'guess_result': {
        setGuessesLeft(event.guesses_left);
        setLastGuessResult({ correct: event.correct, points: event.points, speedBonus: event.speed_bonus });
        if (event.correct) {
          setAnswered(true);
          if (event.celebrity_name) setRevealedName(event.celebrity_name);
        }
        break;
      }
      case 'out_of_guesses': {
        setForfeited(true);
        setRevealedName(event.celebrity_name);
        break;
      }
      case 'opponent_update': {
        if (event.guesses_left !== undefined) setOpponentGuessesLeft(event.guesses_left);
        if (event.answered) setOpponentAnswered(true);
        if (event.wrong_guess) {
          setOpponentWrongFlash(true);
          setTimeout(() => setOpponentWrongFlash(false), 1500);
        }
        break;
      }
      case 'round_end': {
        clearInterval(hintTimerRef.current);
        setPhase('roundEnd');
        setBlur(event.blur ?? 0);
        setImageRevealed(true);
        setRevealedName(event.celebrity_name);
        setRoundEndData(event);
        break;
      }
      case 'match_end': {
        clearInterval(hintTimerRef.current);
        setPhase('matchEnd');
        setMatchEndData(event);
        break;
      }
      case 'chat_message': {
        const msg = { ...event, from: event.from === myKey ? 'you' : 'opponent' };
        setMessages(prev => [...prev, msg]);
        // Increment unread badge only when chat drawer is closed (mobile)
        setChatOpen(prev => {
          if (!prev) setUnreadChat(n => n + 1);
          return prev;
        });
        break;
      }
      case 'skip_vote_sent': {
        setMySkipVoted(true);
        if (event.skips_remaining !== undefined) setSkipsRemaining(event.skips_remaining);
        break;
      }
      case 'skip_vote_received': { setOpponentSkipVoted(true); break; }
      case 'skip_denied':        { setMySkipVoted(false); break; }
      case 'opponent_exited':
      case 'opponent_disconnected': {
        clearInterval(hintTimerRef.current);
        setLastGuessResult({ correct: false, points: 0, speedBonus: 0, message: 'Opponent left the game!' });
        break;
      }
      case 'exit_confirmed':     { onLeave('quit', false); break; }
      case 'reaction': {
        setIncomingReaction(event.emoji);
        setTimeout(() => setIncomingReaction(null), 2000);
        break;
      }
      case 'inactivity_warning': { setInactivityWarning(event.seconds_left); break; }
      default: break;
    }
  }, [onLeave, startHintTimer, myKey]);

  useEffect(() => {
    if (roomData?.onEvent) roomData.onEvent(handleEvent);
  }, [handleEvent, roomData]);

  // ── Actions (identical) ───────────────────────────────────────────────────
  const handleGuess    = (guess)   => emit('submit_guess', { guess });
  const handleChat     = (message) => emit('send_chat', { message });
  const handleSkipVote = () => {
    if (mySkipVoted || skipsRemaining <= 0) return;
    emit('vote_skip');
  };
  const handleExitClick   = () => setShowExitConfirm(true);
  const handleExitCancel  = () => setShowExitConfirm(false);
  const handleExitConfirm = () => { setShowExitConfirm(false); emit('exit_game'); };
  const handlePlayAgain   = () => {
    const didWin = matchEndData?.winner === myKey;
    const isTie  = matchEndData?.is_tie;
    onLeave('play_again', isTie ? null : didWin);
  };
  const handleQuitLobby = () => {
    const didWin = matchEndData?.winner === myKey;
    const isTie  = matchEndData?.is_tie;
    onLeave('quit', isTie ? null : didWin);
  };

  const opponentStatus = opponentAnswered
    ? '✅ Got it!'
    : opponentWrongFlash ? '❌ Wrong!'
    : opponentGuessesLeft === 0 ? '💀 Out of guesses'
    : `${opponentGuessesLeft} guesses left`;

  // ── Sub-screens ───────────────────────────────────────────────────────────
  if (phase === 'roundEnd' && roundEndData) {
    return (
      <RoundEnd
        data={roundEndData}
        myKey={myKey}
        onContinue={() => { setPhase('waiting'); setRoundEndData(null); }}
      />
    );
  }
  if (phase === 'matchEnd' && matchEndData) {
    return (
      <MatchEnd
        data={matchEndData}
        myKey={myKey}
        onPlayAgain={handlePlayAgain}
        onLeave={handleQuitLobby}
      />
    );
  }

  // Shared exit modal
  const ExitModal = showExitConfirm && (
    <div style={s.overlay}>
      <div style={s.modal}>
        <p style={s.modalTitle}>Leave the game?</p>
        <p style={s.modalSub}>You will forfeit and your opponent wins.</p>
        <div style={s.modalBtns}>
          <button style={s.cancelBtn}  onClick={handleExitCancel}>Cancel</button>
          <button style={s.confirmBtn} onClick={handleExitConfirm}>Yes, Leave</button>
        </div>
      </div>
    </div>
  );

  // ── Slide-up animation keyframes injected once ────────────────────────────
  // (inline styles can't do keyframes, so we inject a tiny <style> tag)
  const SlideUpStyle = (
    <style>{`@keyframes grSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // Single scrollable column: header → image → hints → guesses → input → skip
  // Floating 💬 FAB opens a slide-up chat drawer
  // Desktop layout is completely untouched below
  // ═══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div style={m.root}>
        {SlideUpStyle}
        {ExitModal}

        {/* Compact header */}
        <div style={m.header}>
          <span style={m.roundBadge}>Round {round}/3</span>
          {inactivityWarning && <span style={m.warnBadge}>⚠️ {inactivityWarning}s</span>}
          <div style={m.opponentInfo}>
            <span style={m.opponentName}>{opponentName}</span>
            <span style={{
              ...m.opponentStatus,
              color: opponentAnswered ? '#22c55e' : opponentWrongFlash ? '#ef4444' : 'rgba(255,255,255,0.45)',
            }}>
              {opponentStatus}
            </span>
          </div>
          <button style={m.exitBtn} onClick={handleExitClick}>✕</button>
        </div>

        {/* Scrollable single column */}
        <div style={m.scroll}>

          {/* Celebrity image — capped height so hints stay visible */}
          <div style={m.imgWrap}>
            <CelebrityImage
              imageUrl={celebrityImage}
              blur={blur}
              revealed={imageRevealed}
              celebrityName={revealedName}
            />
          </div>

          {/* Hints — always visible below image */}
          <HintPanel
            hints={hints}
            currentHintNumber={currentHintNumber}
            timeLeft={hintTimer}
          />

          {/* Guess dots */}
          <div style={m.guessesRow}>
            {[0,1,2].map(i => (
              <span key={i} style={{
                ...s.guessDot,
                background: i < guessesLeft ? '#6366f1' : 'rgba(255,255,255,0.12)',
              }} />
            ))}
            <span style={s.guessLabel}>{guessesLeft} guesses left</span>
          </div>

          {/* Result banner */}
          {lastGuessResult && (
            <div style={{
              ...s.resultBanner,
              background: lastGuessResult.correct ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${lastGuessResult.correct ? '#22c55e40' : '#ef444440'}`,
            }}>
              {lastGuessResult.message
                ? <span style={{ color: '#fbbf24', fontSize: '14px' }}>{lastGuessResult.message}</span>
                : lastGuessResult.correct
                  ? <>
                      <span style={s.pointsFlash}>+{lastGuessResult.points} pts</span>
                      {lastGuessResult.speedBonus > 0 && <span style={s.speedFlash}>⚡ +{lastGuessResult.speedBonus} speed bonus</span>}
                    </>
                  : <span style={s.wrongFlash}>Wrong guess!</span>
              }
            </div>
          )}

          {/* Revealed name */}
          {(answered || forfeited) && revealedName && (
            <div style={{
              padding: '8px 16px', borderRadius: '10px', textAlign: 'center',
              fontSize: '14px', fontWeight: '700',
              background: answered ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${answered ? '#22c55e40' : '#ef444440'}`,
              color: answered ? '#22c55e' : '#f87171',
            }}>
              {answered ? '✅ ' : '❌ '}{revealedName}
            </div>
          )}

          {/* Guess input */}
          <GuessInput onGuess={handleGuess} answered={answered} forfeited={forfeited} />

          {/* Skip vote — extra bottom padding so FAB doesn't cover it */}
          {phase === 'playing' && !answered && !forfeited && (
            <div style={{ ...s.skipRow, paddingBottom: '72px' }}>
              <button
                style={{
                  ...s.skipBtn,
                  opacity: mySkipVoted || skipsRemaining <= 0 ? 0.4 : 1,
                  cursor:  mySkipVoted || skipsRemaining <= 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={handleSkipVote}
                disabled={mySkipVoted || skipsRemaining <= 0}
              >
                {mySkipVoted ? '⏭️ Waiting for opponent...' : '⏭️ Vote to skip hint'}
              </button>
              {opponentSkipVoted && !mySkipVoted && (
                <span style={s.opponentSkipNote}>Opponent wants to skip!</span>
              )}
              <span style={s.skipsLeft}>{skipsRemaining} of 5 skips remaining</span>
            </div>
          )}
        </div>

        {/* Floating chat button */}
        <button
          style={m.fab}
          onClick={() => { setChatOpen(true); setUnreadChat(0); }}
          aria-label="Open chat"
        >
          💬
          {unreadChat > 0 && (
            <span style={m.fabBadge}>{unreadChat > 9 ? '9+' : unreadChat}</span>
          )}
        </button>

        {/* Incoming reaction */}
        {incomingReaction && <div style={m.reactionFloat}>{incomingReaction}</div>}

        {/* Chat drawer */}
        {chatOpen && (
          <>
            <div
              style={m.backdrop}
              onClick={() => setChatOpen(false)}
            />
            <div style={m.drawer}>
              {/* Drag handle + title + close */}
              <div style={m.drawerTop}>
                <div style={m.drawerHandle} />
                <span style={m.drawerTitle}>Chat · {opponentName}</span>
                <button style={m.drawerClose} onClick={() => setChatOpen(false)}>✕</button>
              </div>
              {/* ChatPanel owns its own scroll + input */}
              <div style={m.drawerBody}>
                <ChatPanel
                  messages={messages}
                  onSend={handleChat}
                  playerName={myName}
                  opponentName={opponentName}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT — byte-for-byte identical to original
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={s.root}>
      {ExitModal}

      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.roundBadge}>Round {round} / 3</span>
          {inactivityWarning && (
            <span style={s.inactivityWarn}>⚠️ Inactive — {inactivityWarning}s left</span>
          )}
        </div>
        <div style={s.headerRight}>
          <div style={s.opponentPill}>
            <span style={s.opponentName}>{opponentName}</span>
            <span style={{
              ...s.opponentStatus,
              color: opponentAnswered ? '#22c55e' : opponentWrongFlash ? '#ef4444' : 'rgba(255,255,255,0.4)',
            }}>
              {opponentStatus}
            </span>
          </div>
          <button style={s.exitBtn} onClick={handleExitClick}>✕ Exit</button>
        </div>
      </div>

      {incomingReaction && <div style={s.reactionBubble}>{incomingReaction}</div>}

      <div style={s.grid}>
        <div style={s.leftCol}>
          <CelebrityImage
            imageUrl={celebrityImage}
            blur={blur}
            revealed={imageRevealed}
            celebrityName={revealedName}
          />

          <div style={s.guessesRow}>
            {[0,1,2].map(i => (
              <span key={i} style={{
                ...s.guessDot,
                background: i < guessesLeft ? '#6366f1' : 'rgba(255,255,255,0.1)',
              }} />
            ))}
            <span style={s.guessLabel}>{guessesLeft} guesses left</span>
          </div>

          {lastGuessResult && (
            <div style={{
              ...s.resultBanner,
              background: lastGuessResult.correct ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${lastGuessResult.correct ? '#22c55e40' : '#ef444440'}`,
            }}>
              {lastGuessResult.message
                ? <span style={{ color: '#fbbf24', fontSize: '14px' }}>{lastGuessResult.message}</span>
                : lastGuessResult.correct
                  ? <>
                      <span style={s.pointsFlash}>+{lastGuessResult.points} pts</span>
                      {lastGuessResult.speedBonus > 0 && <span style={s.speedFlash}>⚡ +{lastGuessResult.speedBonus} speed bonus</span>}
                    </>
                  : <span style={s.wrongFlash}>Wrong guess!</span>
              }
            </div>
          )}

          {(answered || forfeited) && revealedName && (
            <div style={{
              padding: '8px 16px',
              background: answered ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${answered ? '#22c55e40' : '#ef444440'}`,
              borderRadius: '10px', fontSize: '14px', fontWeight: '700',
              color: answered ? '#22c55e' : '#f87171', textAlign: 'center', width: '100%',
            }}>
              {answered ? '✅ ' : '❌ '}{revealedName}
            </div>
          )}

          <GuessInput onGuess={handleGuess} answered={answered} forfeited={forfeited} />

          {phase === 'playing' && !answered && !forfeited && (
            <div style={s.skipRow}>
              <button
                style={{
                  ...s.skipBtn,
                  opacity: mySkipVoted || skipsRemaining <= 0 ? 0.4 : 1,
                  cursor:  mySkipVoted || skipsRemaining <= 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={handleSkipVote}
                disabled={mySkipVoted || skipsRemaining <= 0}
              >
                {mySkipVoted ? '⏭️ Waiting for opponent...' : '⏭️ Vote to skip hint'}
              </button>
              {opponentSkipVoted && !mySkipVoted && (
                <span style={s.opponentSkipNote}>Opponent wants to skip!</span>
              )}
              <span style={s.skipsLeft}>{skipsRemaining} of 5 skips remaining</span>
            </div>
          )}
        </div>

        <div style={s.rightCol}>
          <HintPanel
            hints={hints}
            currentHintNumber={currentHintNumber}
            timeLeft={hintTimer}
          />
          <ChatPanel
            messages={messages}
            onSend={handleChat}
            playerName={myName}
            opponentName={opponentName}
          />
        </div>
      </div>
    </div>
  );
}

// ─── DESKTOP STYLES — identical to original ───────────────────────────────────
const s = {
  root: { minHeight: '100vh', background: '#0d0d1a', color: '#fff', fontFamily: "'Syne', sans-serif", display: 'flex', flexDirection: 'column' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', maxWidth: '340px', width: '90%', textAlign: 'center' },
  modalTitle: { margin: '0 0 8px', fontSize: '20px', fontWeight: '800', color: '#fff' },
  modalSub: { margin: '0 0 24px', fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.5' },
  modalBtns: { display: 'flex', gap: '12px' },
  cancelBtn: { flex: 1, padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  confirmBtn: { flex: 1, padding: '12px', borderRadius: '12px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  roundBadge: { padding: '6px 14px', borderRadius: '20px', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', fontSize: '13px', fontWeight: '700', color: '#a5b4fc' },
  inactivityWarn: { fontSize: '12px', color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '4px 10px', borderRadius: '8px' },
  opponentPill: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '20px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' },
  opponentName: { fontSize: '13px', fontWeight: '700', color: '#fff' },
  opponentStatus: { fontSize: '11px', transition: 'color 0.3s' },
  exitBtn: { padding: '6px 14px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '12px', cursor: 'pointer', fontWeight: '600' },
  reactionBubble: { position: 'fixed', top: '80px', right: '24px', fontSize: '48px', zIndex: 100, pointerEvents: 'none' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '20px 24px', flex: 1 },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' },
  rightCol: { display: 'flex', flexDirection: 'column', gap: '16px' },
  guessesRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  guessDot: { width: '12px', height: '12px', borderRadius: '50%', transition: 'background 0.3s' },
  guessLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginLeft: '4px' },
  resultBanner: { width: '100%', padding: '10px 14px', borderRadius: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  pointsFlash: { fontSize: '16px', fontWeight: '800', color: '#22c55e' },
  speedFlash: { fontSize: '12px', color: '#fbbf24' },
  wrongFlash: { fontSize: '14px', color: '#ef4444', fontWeight: '600' },
  skipRow: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', width: '100%' },
  skipBtn: { padding: '8px 16px', borderRadius: '10px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', fontSize: '13px', fontWeight: '600', width: '100%', transition: 'opacity 0.2s' },
  opponentSkipNote: { fontSize: '12px', color: '#fbbf24', fontWeight: '600' },
  skipsLeft: { fontSize: '11px', color: 'rgba(255,255,255,0.3)' },
};

// ─── MOBILE-ONLY STYLES ───────────────────────────────────────────────────────
const m = {
  root: {
    minHeight: '100vh',
    background: '#0d0d1a',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'hidden',
  },

  // Single-row compact header
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  },
  roundBadge: {
    padding: '4px 10px', borderRadius: '14px', whiteSpace: 'nowrap', flexShrink: 0,
    background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)',
    fontSize: '11px', fontWeight: '700', color: '#a5b4fc',
  },
  warnBadge: {
    fontSize: '10px', color: '#fbbf24', background: 'rgba(251,191,36,0.1)',
    padding: '3px 7px', borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0,
  },
  opponentInfo: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', minWidth: 0, overflow: 'hidden',
  },
  opponentName: {
    fontSize: '12px', fontWeight: '700', color: '#fff',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
  },
  opponentStatus: { fontSize: '10px', transition: 'color 0.3s', whiteSpace: 'nowrap' },
  exitBtn: {
    padding: '5px 10px', borderRadius: '8px', flexShrink: 0,
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#f87171', fontSize: '13px', cursor: 'pointer', fontWeight: '700',
  },

  // Vertical scrollable area
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },

  // Image constrained to ~40% of typical mobile screen height
  imgWrap: {
    width: '100%',
    maxHeight: '240px',
    overflow: 'hidden',
    borderRadius: '12px',
    flexShrink: 0,
  },

  guessesRow: { display: 'flex', alignItems: 'center', gap: '8px' },

  // Floating Action Button — chat
  fab: {
    position: 'fixed',
    bottom: 'calc(18px + env(safe-area-inset-bottom, 0px))',
    right: '16px',
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(99,102,241,0.55)',
    zIndex: 80,
  },
  fabBadge: {
    position: 'absolute', top: '-3px', right: '-3px',
    minWidth: '18px', height: '18px',
    background: '#ef4444', borderRadius: '9px',
    fontSize: '10px', fontWeight: '800', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 4px', border: '2px solid #0d0d1a',
  },

  reactionFloat: {
    position: 'fixed', top: '56px', right: '14px',
    fontSize: '40px', zIndex: 100, pointerEvents: 'none',
  },

  // Drawer backdrop
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    zIndex: 110,
  },

  // Slide-up chat drawer — 72 % of viewport height
  drawer: {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    height: '72vh',
    background: '#1a1a2e',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px 20px 0 0',
    zIndex: 111,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
    animation: 'grSlideUp 0.28s cubic-bezier(0.25,1,0.5,1)',
  },

  drawerTop: {
    display: 'flex', alignItems: 'center',
    padding: '16px 16px 10px',
    flexShrink: 0,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    position: 'relative',
  },
  drawerHandle: {
    position: 'absolute', top: '8px', left: '50%',
    transform: 'translateX(-50%)',
    width: '36px', height: '4px', borderRadius: '2px',
    background: 'rgba(255,255,255,0.2)',
  },
  drawerTitle: {
    flex: 1, textAlign: 'center',
    fontSize: '14px', fontWeight: '700', color: '#fff',
  },
  drawerClose: {
    background: 'rgba(255,255,255,0.08)', border: 'none',
    color: 'rgba(255,255,255,0.6)', fontSize: '14px',
    width: '28px', height: '28px', borderRadius: '50%',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },

  // ChatPanel fills this and manages its own layout
  drawerBody: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
};
